import { forwardRef } from "react";

import { Box, useTheme } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

import { HoverBoxProps } from "./types";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { NextLink } from "@/components/NextLink/NextLink";

export type HoverBoxBaseProps = HoverBoxProps & {
  rootSx: SxProps<Theme>;
  contentSx: SxProps<Theme>;
};

type HoverBoxContentInnerProps = Omit<HoverBoxBaseProps, "href" | "target">;

const HoverBoxContentInner = forwardRef<HTMLDivElement, HoverBoxContentInnerProps>(
  ({ children, slots, slotProps, onClick, sx, rootSx, contentSx, "data-test": dataTest }, ref) => {
    const { isPhoneScreen } = useMediaQuery();
    const theme = useTheme();
    const hasHeader = Boolean(slots?.header);
    const actions = slots?.actions;
    const preview = slots?.preview;

    return (
      <Box onClick={onClick} sx={mergeSx(rootSx, sx)} data-test={dataTest} ref={ref}>
        {hasHeader && (
          <Box
            display="flex"
            alignItems={isPhoneScreen ? "stretch" : "center"}
            justifyContent="space-between"
            flexDirection={isPhoneScreen ? "column" : "row"}
            gap={2}
            sx={slotProps?.wrapper?.sx}
          >
            {preview && !isPhoneScreen && <Box margin={theme.spacing(0, 2, 0, 2)}>{preview}</Box>}
            <Box flex={1} sx={{ minWidth: 0 }}>
              <Box sx={contentSx}>{slots?.header}</Box>
              <Box flexGrow={1}>{children}</Box>
            </Box>
            {actions && (
              <Box sx={isPhoneScreen ? { width: "fit-content" } : { margin: "auto" }}>
                <div>{actions}</div>
              </Box>
            )}
          </Box>
        )}
        {!hasHeader && <Box sx={mergeSx(contentSx, slotProps?.wrapper?.sx)}>{children}</Box>}
      </Box>
    );
  }
);

HoverBoxContentInner.displayName = "HoverBoxContentInner";

/**
 * Shared structural HoverBox: layout (slots header/preview/actions), link wrapper.
 * Styling is injected via rootSx and contentSx so Dashboard and Forms can each apply their theme.
 */
export const HoverBoxBase = forwardRef<HTMLDivElement, HoverBoxBaseProps>(
  ({ href, target, rootSx, contentSx, ...rest }, ref) => {
    const content = <HoverBoxContentInner {...rest} rootSx={rootSx} contentSx={contentSx} ref={ref} />;

    if (!href) {
      return content;
    }

    if (target === "_blank") {
      return (
        <a
          href={href}
          target={target}
          rel="noopener noreferrer"
          style={{ textDecoration: "none", color: "unset", maxWidth: "100%" }}
        >
          {content}
        </a>
      );
    }

    return (
      <NextLink href={href} target={target}>
        {content}
      </NextLink>
    );
  }
);

HoverBoxBase.displayName = "HoverBoxBase";
